/**
 * Admin Users API client — FE-03/FE-04.
 * Calls GET /admin/users, GET /admin/users/:id,
 * POST /admin/users/:id/freeze, POST /admin/users/:id/unfreeze.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type UserStatus = "active" | "frozen" | "deactivated";

export type UserSortField =
  | "created_at"
  | "display_name"
  | "email"
  | "plan_type"
  | "token_balance";

export type UserSortDirection = "asc" | "desc";

export type UserListStats = {
  readonly total: number;
  readonly by_status: Record<UserStatus, number>;
  readonly by_plan: Record<"free" | "pro" | "premium" | "max", number>;
};

export type AdminUserSummary = {
  readonly id: string;
  readonly email: string;
  readonly custom_id: string | null;
  readonly display_name: string;
  readonly plan_type: string;
  readonly token_balance: number;
  readonly storage_used_bytes: number;
  readonly storage_limit_bytes: number;
  readonly status: UserStatus;
  readonly created_at: string;
};

export type SummaryJobBrief = {
  readonly id: string;
  readonly target_type: string;
  readonly status: string;
  readonly created_at: string;
};

export type AuditRecordBrief = {
  readonly id: string;
  readonly action: string;
  readonly created_at: string;
};

export type BillingEventBrief = {
  readonly id: string;
  readonly entry_type: string;
  readonly amount: number;
  readonly created_at: string;
};

export type AdminUserDetail = {
  readonly id: string;
  readonly email: string;
  readonly custom_id: string | null;
  readonly display_name: string;
  readonly avatar_url: string | null;
  readonly bio: string | null;
  readonly language: string | null;
  readonly theme: string | null;
  readonly last_country: string | null;
  readonly last_locale: string | null;
  readonly plan_type: string;
  readonly plan_status: string;
  readonly subscription_tokens_used: number;
  readonly subscription_tokens_limit: number;
  readonly topup_tokens_remaining: number;
  readonly storage_used_bytes: number;
  readonly storage_limit_bytes: number;
  readonly device_count: number;
  readonly status: UserStatus;
  readonly frozen_at: string | null;
  readonly deactivated_at: string | null;
  readonly created_at: string;
  readonly recent_summary_jobs: ReadonlyArray<SummaryJobBrief>;
  readonly recent_audit_records: ReadonlyArray<AuditRecordBrief>;
  readonly recent_billing_events: ReadonlyArray<BillingEventBrief>;
};

export type ListUsersParams = {
  readonly q?: string;
  readonly status?: UserStatus;
  readonly plan?: string;
  readonly limit?: number;
  readonly offset?: number;
  readonly sort_by?: UserSortField;
  readonly sort_dir?: UserSortDirection;
};

type ListUsersResponse = {
  readonly items: AdminUserSummary[];
  readonly total: number;
  readonly stats: unknown;
};

type ListUsersData = {
  readonly items: AdminUserSummary[];
  readonly total: number;
  readonly stats: UserListStats;
};

// ─── API calls ───

export async function fetchUsers(
  params: ListUsersParams,
): Promise<ListUsersData> {
  const searchParams = new URLSearchParams();
  const q = params.q?.trim();
  if (q) searchParams.set("q", q);
  if (params.status) searchParams.set("status", params.status);
  if (params.plan) searchParams.set("plan", params.plan);
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.offset) searchParams.set("offset", String(params.offset));
  if (params.sort_by) searchParams.set("sort_by", params.sort_by);
  if (params.sort_dir) searchParams.set("sort_dir", params.sort_dir);

  const qs = searchParams.toString();
  const path = qs ? `/admin/users?${qs}` : "/admin/users";
  const res = await apiFetch<{ data: ListUsersResponse }>(path);
  return {
    ...res.data,
    stats: normalizeUserListStats(res.data.stats, res.data.total),
  };
}

export function normalizeUserListStats(
  stats: unknown,
  fallbackTotal: number,
): UserListStats {
  if (!isRecord(stats)) return emptyUserListStats(fallbackTotal);
  const byStatus = isRecord(stats.by_status) ? stats.by_status : {};
  const byPlan = isRecord(stats.by_plan) ? stats.by_plan : {};
  return {
    total: safeCount(stats.total, fallbackTotal),
    by_status: {
      active: safeCount(byStatus.active, 0),
      frozen: safeCount(byStatus.frozen, 0),
      deactivated: safeCount(byStatus.deactivated, 0),
    },
    by_plan: {
      free: safeCount(byPlan.free, 0),
      pro: safeCount(byPlan.pro, 0),
      premium: safeCount(byPlan.premium, 0),
      max: safeCount(byPlan.max, 0),
    },
  };
}

function emptyUserListStats(total: number): UserListStats {
  return {
    total,
    by_status: { active: 0, frozen: 0, deactivated: 0 },
    by_plan: { free: 0, pro: 0, premium: 0, max: 0 },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function safeCount(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : fallback;
}

export async function fetchUserDetail(id: string): Promise<AdminUserDetail> {
  const res = await apiFetch<{ data: AdminUserDetail }>(`/admin/users/${id}`);
  return res.data;
}

export async function freezeUser(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { frozen: boolean } }>(`/admin/users/${id}/freeze`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function unfreezeUser(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { unfrozen: boolean } }>(
    `/admin/users/${id}/unfreeze`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}
